import { derive } from '@traits-ts/core'
import { useNotificationCreator } from '../../../composables/useNotificationCreator.js'
import { useSubKrewesStore } from '../../../stores/subkrewes.js'
import type { TaskActionButton, TaskListAction } from '../../types/task.js'
import { AsBaseTask } from '../traits/AsBaseTask.js'
import { WithAssignment } from '../traits/WithAssignment.js'
import { WithNotifications } from '../traits/WithNotifications.js'
import { WithReferences } from '../traits/WithReferences.js'
import { WithUIConfiguration } from '../traits/WithUIConfiguration.js'

/**
 * SubKreweApprovalTask
 * Represents an admin task to approve or deny a new SubKrewe
 */
export class SubKreweApprovalTask extends derive(
  AsBaseTask,
  WithAssignment,
  WithReferences,
  WithUIConfiguration,
  WithNotifications,
) {
  /**
   * Create a new SubKrewe approval task
   * @param subkreweId ID of the SubKrewe pending approval
   * @param subkreweName Name of the SubKrewe
   * @param captainId ID of the SubKrewe captain
   * @param captainName Name of the SubKrewe captain
   */
  constructor(
    subkreweId: string,
    subkreweName: string = '',
    captainId?: string,
    captainName?: string,
  ) {
    super('SubKreweApprovalTask')

    // Set basic task properties
    this.title = `Approve SubKrewe: ${subkreweName || 'New SubKrewe'}`
    this.description = `Review and approve the registration of ${subkreweName || 'a new SubKrewe'}.`
    this.taskType = 'admin'
    this.priority = 'medium'

    // Store SubKrewe data in metadata
    this.metadata = {
      subkreweId,
      subkreweName,
      captainId,
      captainName,
      // Add this flag to control generic notifications
      suppressCreationNotification: true,
    }

    // Assign to the SubKrewe so it shows up scoped to that entity
    this.assign(subkreweId, 'subkrewe')

    // Add references to both entities
    this.addReference(subkreweId, 'subkrewe', 'target')
    if (captainId) {
      this.addReference(captainId, 'member', 'captain')
    }

    // Record that we're using these traits
    this.traits = ['WithAssignment', 'WithReferences', 'WithUIConfiguration', 'WithNotifications']
  }

  /**
   * Approve the SubKrewe
   * This updates the task status and triggers the actual SubKrewe approval
   */
  async approve(): Promise<boolean> {
    if (this.status === 'completed') {
      return true
    }

    try {
      // Update the SubKrewe approval status
      if (typeof this.metadata.subkreweId === 'string') {
        const subkrewesStore = useSubKrewesStore()
        const subkrewe = await subkrewesStore.fetchSubKrewe(this.metadata.subkreweId)

        if (subkrewe) {
          await subkrewesStore.updateSubKrewe(subkrewe, {
            approved: 'Yes',
          })
        }
      }

      // Set status to completed
      this.status = 'completed'
      this.completedAt = new Date()
      this.updatedAt = new Date()

      // Add metadata about approval
      this.metadata.approvedAt = new Date()
      this.metadata.approved = true

      // Send notification to captain(s)
      if (typeof this.metadata.captainId === 'string') {
        await this.notifyApproved(
          this.metadata.captainId,
          this.metadata.subkreweId as string,
        )
      }

      return true
    }
    catch (error) {
      console.error('Error approving SubKrewe:', error)
      return false
    }
  }

  /**
   * Deny the SubKrewe registration
   * This updates the task status and records that the request was denied
   */
  async deny(reason?: string): Promise<boolean> {
    if (this.status === 'canceled') {
      return true
    }

    try {
      // Update the SubKrewe approval status
      if (typeof this.metadata.subkreweId === 'string') {
        const subkrewesStore = useSubKrewesStore()
        const subkrewe = await subkrewesStore.fetchSubKrewe(this.metadata.subkreweId)

        if (subkrewe) {
          await subkrewesStore.updateSubKrewe(subkrewe, {
            approved: 'No',
            decline_reason: reason,
          })
        }
      }

      // Set status to canceled
      this.status = 'canceled'
      this.completedAt = new Date()
      this.updatedAt = new Date()

      // Add metadata about denial
      this.metadata.deniedAt = new Date()
      this.metadata.approved = false

      // Store denial reason if provided
      if (reason) {
        this.metadata.denialReason = reason
      }

      // Send notification to captain(s)
      if (typeof this.metadata.captainId === 'string') {
        await this.notifyDenied(
          this.metadata.captainId,
          this.metadata.subkreweId as string,
          reason,
        )
      }

      return true
    }
    catch (error) {
      console.error('Error denying SubKrewe:', error)
      return false
    }
  }

  /**
   * Send a notification that the SubKrewe was approved
   */
  private async notifyApproved(
    captainId: string,
    subkreweId: string,
  ): Promise<string | null> {
    try {
      const notificationCreator = useNotificationCreator()
      const subkreweName = this.metadata.subkreweName as string || 'your SubKrewe'

      return await notificationCreator.createMembershipNotification(
        captainId,
        'SubKrewe Approved! Now Activate!',
        `Your SubKrewe "${subkreweName}" has been approved by the Overlords! However, <strong>your SubKrewe is not yet active</strong>. To activate it and participate in the parade, you need to pay your captain dues. Click below to complete payment and activate your SubKrewe!`,
        subkreweId,
        {
          priority: 'high',
          sendEmail: true,
          emailTemplate: 'membership',
          actionText: 'Activate Your SubKrewe',
          metadata: {
            subkreweId,
            subkreweName,
          },
        },
      )
    }
    catch (error) {
      console.error('Error sending approval notification:', error)
      return null
    }
  }

  /**
   * Send a notification that the SubKrewe was denied
   */
  private async notifyDenied(
    captainId: string,
    subkreweId: string,
    reason?: string,
  ): Promise<string | null> {
    try {
      const notificationCreator = useNotificationCreator()
      const subkreweName = this.metadata.subkreweName as string || 'your SubKrewe'

      return await notificationCreator.createMembershipNotification(
        captainId,
        'SubKrewe Registration Denied',
        `Your SubKrewe "${subkreweName}" registration was not approved.${reason ? ` Reason: ${reason}` : ''}`,
        subkreweId,
        {
          priority: 'high',
          sendEmail: true,
          emailTemplate: 'membership',
          metadata: {
            subkreweId,
            subkreweName,
            reason,
          },
        },
      )
    }
    catch (error) {
      console.error('Error sending denial notification:', error)
      return null
    }
  }

  /**
   * Send a notification that a new SubKrewe approval task was created
   * This notifies admins (and band admins for bands) that a new SubKrewe needs approval
   */
  async notifySubmitted(): Promise<string | null> {
    try {
      // Use the server API to send notifications to all admins
      const subkreweName = this.metadata.subkreweName as string || 'New SubKrewe'
      const captainName = this.metadata.captainName as string || 'A member'
      const isBand = this.metadata.isBand as boolean || false

      // Determine which endpoint to use based on whether this is a band
      const endpoint = isBand
        ? '/api/notifications/notify-admins-and-band-admins'
        : '/api/notifications/notify-admins'

      // Call the appropriate notification API
      const response = await $fetch(endpoint, {
        method: 'POST',
        body: {
          title: 'New SubKrewe Registration',
          body: `${captainName} has registered "${subkreweName}" that requires approval.`,
          linkType: 'task',
          linkTarget: this.id,
          priority: 'normal',
          sendEmail: true, // Add email notification for better visibility
          emailTemplate: 'admin', // Use the admin email template
          metadata: {
            subkreweId: this.metadata.subkreweId,
            subkreweName,
            captainId: this.metadata.captainId,
            captainName,
            taskId: this.id,
            isBand,
          },
        },
      })

      // Log success for debugging
      if (response?.success) {
        const recipientType = isBand ? 'admins and band admins' : 'admins'
        console.warn(`[SubKreweApprovalTask] Successfully sent notifications to ${response.adminsCount || response.totalCount} ${recipientType}`)
        return 'server-handled' // Return a non-null value to indicate success
      }
      else {
        console.warn(`[SubKreweApprovalTask] Issue sending notifications: ${response?.message || 'Unknown error'}`)
        return null
      }
    }
    catch (error) {
      console.error('Error sending SubKrewe approval notifications to admins:', error)
      return null
    }
  }

  /**
   * Override getActionButtons to customize actions for SubKrewe approval
   */
  override getActionButtons() {
    const buttons: TaskActionButton[] = []

    // Only show buttons for pending or in-progress requests
    if (this.status === 'pending' || this.status === 'in_progress') {
      buttons.push({
        label: 'Deny',
        icon: 'pi pi-times',
        action: 'deny',
        danger: true,
      })

      buttons.push({
        label: 'Approve',
        icon: 'pi pi-check',
        action: 'approve',
        primary: true,
      })
    }

    return buttons
  }

  /**
   * Override getListActions to provide quick actions in list views
   */
  override getListActions() {
    const actions: TaskListAction[] = []

    // Only show actions for pending or in-progress requests
    if (this.status === 'pending' || this.status === 'in_progress') {
      actions.push({
        label: 'Approve',
        icon: 'pi pi-check',
        action: 'approve',
        primary: true,
      })

      actions.push({
        label: 'Deny',
        icon: 'pi pi-times',
        action: 'deny',
        danger: true,
      })
    }

    return actions
  }

  /**
   * Override getTaskColor to customize the task color
   */
  override getTaskColor() {
    return 'var(--purple-600)' // A distinct color for approval tasks
  }

  /**
   * Override getTaskIcon to use a specific icon for approval tasks
   */
  override getTaskIcon() {
    return 'pi pi-shield'
  }

  /**
   * Execute the task
   * For approval tasks, this doesn't do anything directly
   * as admins need to explicitly approve or deny
   */
  override async execute(): Promise<boolean> {
    // For approval requests that are pending, set to in_progress but return false
    // to indicate that execution is not complete
    if (this.status === 'pending') {
      this.status = 'in_progress'
      this.updatedAt = new Date()
      return false
    }

    // For already completed/canceled tasks, just return true
    return true
  }

  /**
   * Define which metadata fields should be displayed in the UI
   * Enhanced to fetch and use current SubKrewe data
   */
  getMetadataDisplayFields() {
    return [
      {
        key: 'captainName',
        label: 'Captain',
        icon: 'pi pi-user',
        // Fix: Check if captain name exists AND is not empty string
        condition: task => !!task.metadata.captainName && task.metadata.captainName !== '',
      },
      {
        key: 'denialReason',
        label: 'Denial Reason',
        icon: 'pi pi-times-circle',
        condition: task => !!task.metadata.denialReason && task.status === 'canceled',
      },
      // Basic SubKrewe information fields
      {
        key: 'subkreweName',
        label: 'SubKrewe Name',
        icon: 'pi pi-tag',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.name || task.metadata.subkreweName || 'Unknown SubKrewe'
        },
        condition: () => true,
      },
      {
        key: 'description',
        label: 'Description',
        icon: 'pi pi-file-edit',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.description || task.metadata.description || 'No description provided'
        },
        condition: () => true,
      },
      {
        key: 'estimated_members',
        label: 'Estimated Members',
        icon: 'pi pi-users',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.estimated_members || task.metadata.estimated_members || 'Not specified'
        },
        condition: () => true,
      },

      // Admission policy information
      {
        key: 'admission_policy',
        label: 'Admission Policy',
        icon: 'pi pi-shield',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.admission_policy || task.metadata.admission_policy || 'Not specified'
        },
        condition: () => true,
      },
      {
        key: 'admission_restrictions',
        label: 'Admission Restrictions',
        icon: 'pi pi-lock',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.admission_restrictions || task.metadata.admission_restrictions || 'None'
        },
        displayCondition: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          const policy = subkrewe?.admission_policy || task.metadata.admission_policy
          return policy === 'restricted' || policy === 'referral'
        },
      },

      // Contraption information
      {
        key: 'contraption',
        label: 'Has Contraption',
        icon: 'pi pi-cog',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          const hasContraption = subkrewe?.contraption ?? task.metadata.contraption
          return hasContraption ? 'Yes' : 'No'
        },
        condition: () => true,
      },
      {
        key: 'contraption_info',
        label: 'Contraption Details',
        icon: 'pi pi-info-circle',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.contraption_info || task.metadata.contraption_info || 'No details provided'
        },
        displayCondition: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.contraption || task.metadata.contraption
        },
      },

      // Music information
      {
        key: 'music',
        label: 'Music Type',
        icon: 'pi pi-volume-up',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.music || task.metadata.music || 'Not specified'
        },
        condition: () => true,
      },
      {
        key: 'music_other',
        label: 'Music Details',
        icon: 'pi pi-info-circle',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.music_other || task.metadata.music_other || 'No details provided'
        },
        displayCondition: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return (subkrewe?.music || task.metadata.music) === 'other'
        },
      },

      // Parade status
      {
        key: 'rolling_next_parade',
        label: 'Rolling in Next Parade',
        icon: 'pi pi-calendar',
        displayValueResolver: async (task) => {
          const subkrewe = await this.fetchCurrentSubKrewe()
          return subkrewe?.rolling_next_parade || task.metadata.rolling_next_parade || 'Unknown'
        },
        condition: () => true,
      },

      // Captain information
      {
        key: 'captainName',
        label: 'Captain',
        icon: 'pi pi-user',
        displayValueResolver: async (task) => {
          // First try to get from current SubKrewe
          const subkrewe = await this.fetchCurrentSubKrewe()
          if (subkrewe?.captains && subkrewe.captains.length > 0) {
            return subkrewe.captains[0]?.name || task.metadata.captainName || 'Unknown'
          }
          return task.metadata.captainName || 'Unknown'
        },
        condition: () => true,
      },

      // Approval/Denial
      {
        key: 'denialReason',
        label: 'Denial Reason',
        icon: 'pi pi-times-circle',
        condition: task => !!task.metadata.denialReason && task.status === 'canceled',
      },
    ]
  }

  /**
   * Helper method to fetch the current SubKrewe data
   * @returns The current SubKrewe or null if not found
   */
  async fetchCurrentSubKrewe(): Promise<SubKrewe | null> {
    try {
      if (!this.metadata.subkreweId) {
        return null
      }

      const subkrewesStore = useSubKrewesStore()
      return await subkrewesStore.fetchSubKrewe(this.metadata.subkreweId as string)
    }
    catch (error) {
      console.error('Error fetching current SubKrewe:', error)
      return null
    }
  }
}
